import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

/**
 * Die Eingabebausteine des A+S-Plan-Moduls.
 *
 * ═══ WARUM BEIM VERLASSEN GESPEICHERT WIRD UND NICHT BEI JEDEM ZEICHEN ════
 * Jeder Schreibvorgang laedt das Buendel neu (siehe `useAsplan`). Bei einem
 * Neuladen je Tastendruck verliert das Feld den Fokus, und der Anwender tippt
 * ins Leere — genau dieser Fehler hat im Repo schon einmal einen Editor
 * unbrauchbar gemacht (Vorankuendigung, Freitext-Migration).
 *
 * Deshalb: der Entwurf liegt lokal, gespeichert wird beim Verlassen des
 * Feldes und nur, wenn sich etwas geaendert hat. Solange getippt wird, folgt
 * die Anzeige dem Entwurf und nicht dem gespeicherten Stand — sonst
 * ueberschriebe ein Neuladen die laufende Eingabe.
 */

interface TextProps {
    label?: string;
    wert: string;
    gesperrt?: boolean;
    mehrzeilig?: boolean;
    platzhalter?: string;
    hinweis?: string;
    breit?: boolean;
    onSpeichern: (wert: string) => void;
}

export const TextFeld: React.FC<TextProps> = ({
    label, wert, gesperrt, mehrzeilig, platzhalter, hinweis, breit, onSpeichern,
}) => {
    const [entwurf, setEntwurf] = useState(wert);
    const [getippt, setGetippt] = useState(false);
    const anzeige = getippt ? entwurf : wert;

    const beenden = () => {
        if (getippt && entwurf !== wert) onSpeichern(entwurf);
        setGetippt(false);
    };

    const gemeinsam = {
        value: anzeige,
        disabled: gesperrt,
        placeholder: platzhalter,
        onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setGetippt(true);
            setEntwurf(e.target.value);
        },
        onBlur: beenden,
        className: 'w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-sm '
            + 'disabled:bg-slate-50 disabled:text-slate-400',
    };

    return (
        <label className={breit ? 'block w-full' : 'block'}>
            {label && <span className="block text-[11px] font-bold text-slate-600 mb-0.5">{label}</span>}
            {mehrzeilig ? <textarea rows={2} {...gemeinsam} /> : <input {...gemeinsam} />}
            {hinweis && <span className="block text-[10px] text-slate-500 mt-0.5">{hinweis}</span>}
        </label>
    );
};

/**
 * Ein Zahlenfeld, das auch Kommazahlen im Werden vertraegt.
 *
 * `type="number"` waere hier falsch: es verschluckt „2.“ und „0,25“ waehrend
 * des Tippens, weil der Zwischenzustand kein gueltiger Zahlenwert ist. Genau
 * das ist im Repo bei den Mindeststunden passiert. Deshalb ein Textfeld mit
 * eigener Umwandlung — und das Komma gilt als Dezimaltrennzeichen.
 */
export const ZahlFeld: React.FC<{
    label?: string;
    wert: number | null;
    gesperrt?: boolean;
    platzhalter?: string;
    einheit?: string;
    onSpeichern: (wert: number | null) => void;
}> = ({ label, wert, gesperrt, platzhalter, einheit, onSpeichern }) => {
    const alsText = wert === null || wert === undefined ? '' : String(wert).replace('.', ',');
    const [entwurf, setEntwurf] = useState(alsText);
    const [getippt, setGetippt] = useState(false);

    return (
        <label className="block">
            {label && <span className="block text-[11px] font-bold text-slate-600 mb-0.5">{label}</span>}
            <span className="flex items-center gap-1">
                <input
                    value={getippt ? entwurf : alsText}
                    disabled={gesperrt}
                    placeholder={platzhalter}
                    inputMode="decimal"
                    onChange={e => { setGetippt(true); setEntwurf(e.target.value); }}
                    onBlur={() => {
                        if (getippt) {
                            const roh = entwurf.replace(',', '.').trim();
                            const zahl = roh === '' ? null : Number(roh);
                            // Unsinn wird verworfen statt als NaN gespeichert.
                            if (zahl === null || Number.isFinite(zahl)) onSpeichern(zahl);
                        }
                        setGetippt(false);
                    }}
                    className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-sm
                               disabled:bg-slate-50 disabled:text-slate-400"
                />
                {einheit && <span className="text-xs text-slate-500 shrink-0">{einheit}</span>}
            </span>
        </label>
    );
};

export const AuswahlFeld = <T extends string>({
    label, wert, optionen, gesperrt, onSpeichern,
}: {
    label?: string;
    wert: T | '';
    optionen: { wert: T; label: string }[];
    gesperrt?: boolean;
    onSpeichern: (wert: T) => void;
}) => (
    <label className="block">
        {label && <span className="block text-[11px] font-bold text-slate-600 mb-0.5">{label}</span>}
        <select
            value={wert}
            disabled={gesperrt}
            onChange={e => onSpeichern(e.target.value as T)}
            className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-sm
                       disabled:bg-slate-50 disabled:text-slate-400"
        >
            <option value="">bitte wählen</option>
            {optionen.map(o => <option key={o.wert} value={o.wert}>{o.label}</option>)}
        </select>
    </label>
);

export const HakenFeld: React.FC<{
    label: string;
    wert: boolean;
    gesperrt?: boolean;
    onSpeichern: (wert: boolean) => void;
}> = ({ label, wert, gesperrt, onSpeichern }) => (
    <label className="inline-flex items-center gap-1.5 text-xs text-slate-700">
        <input
            type="checkbox"
            checked={wert}
            disabled={gesperrt}
            onChange={e => onSpeichern(e.target.checked)}
        />
        {label}
    </label>
);

/** Eine Zeile mit Löschknopf — die Grundform aller Listen dieses Moduls. */
export const ZeilenKarte: React.FC<{
    kopf?: React.ReactNode;
    gesperrt?: boolean;
    onLoeschen?: () => void;
    loeschTitel?: string;
    children: React.ReactNode;
}> = ({ kopf, gesperrt, onLoeschen, loeschTitel, children }) => (
    <li className="border border-slate-200 rounded-xl p-3">
        <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
                {kopf && <div className="mb-2">{kopf}</div>}
                {children}
            </div>
            {onLoeschen && !gesperrt && (
                <button
                    type="button"
                    onClick={onLoeschen}
                    title={loeschTitel ?? 'Löschen'}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                >
                    <Trash2 className="w-4 h-4" />
                </button>
            )}
        </div>
    </li>
);

/**
 * Die Zeile zum Anlegen.
 *
 * Bewusst mit EINEM Pflichtfeld: alles Weitere wird an der angelegten Zeile
 * ausgefuellt. Ein Anlegedialog mit zwölf Feldern wird abgebrochen; eine
 * Zeile, die schon existiert, wird fertig gepflegt.
 */
export const NeuZeile: React.FC<{
    platzhalter: string;
    knopf: string;
    gesperrt?: boolean;
    breite?: string;
    onAnlegen: (wert: string) => void | Promise<unknown>;
}> = ({ platzhalter, knopf, gesperrt, breite = 'w-56', onAnlegen }) => {
    const [wert, setWert] = useState('');
    if (gesperrt) return null;

    const ausloesen = async () => {
        const t = wert.trim();
        if (!t) return;
        setWert('');
        await onAnlegen(t);
    };

    return (
        <div className="flex gap-2 mb-3">
            <input
                value={wert}
                onChange={e => setWert(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') void ausloesen(); }}
                placeholder={platzhalter}
                className={`px-3 py-2 border border-slate-300 rounded-lg text-sm ${breite}`}
            />
            <button
                type="button"
                disabled={!wert.trim()}
                onClick={() => void ausloesen()}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-stone-700 text-white
                           rounded-lg text-xs font-bold disabled:opacity-40"
            >
                <Plus className="w-3.5 h-3.5" /> {knopf}
            </button>
        </div>
    );
};

/** Mehrere Werte als Marken mit Zufügen und Entfernen (z. B. Feldnummern). */
export const MarkenFeld: React.FC<{
    label: string;
    werte: string[];
    vorschlaege?: string[];
    gesperrt?: boolean;
    platzhalter?: string;
    onSpeichern: (werte: string[]) => void;
}> = ({ label, werte, vorschlaege, gesperrt, platzhalter, onSpeichern }) => {
    const [neu, setNeu] = useState('');
    const zufuegen = (w: string) => {
        const t = w.trim();
        if (!t || werte.includes(t)) return;
        onSpeichern([...werte, t]);
        setNeu('');
    };

    return (
        <div>
            <span className="block text-[11px] font-bold text-slate-600 mb-0.5">{label}</span>
            <div className="flex flex-wrap gap-1.5 mb-1.5">
                {werte.length === 0 && <span className="text-xs text-slate-400">keine</span>}
                {werte.map(w => (
                    <span
                        key={w}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100
                                   rounded text-xs font-semibold text-slate-700"
                    >
                        {w}
                        {!gesperrt && (
                            <button
                                type="button"
                                onClick={() => onSpeichern(werte.filter(x => x !== w))}
                                className="text-slate-400 hover:text-red-600"
                                aria-label={`${w} entfernen`}
                            >
                                ×
                            </button>
                        )}
                    </span>
                ))}
            </div>
            {!gesperrt && (
                <div className="flex gap-1.5 flex-wrap">
                    <input
                        value={neu}
                        onChange={e => setNeu(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') zufuegen(neu); }}
                        placeholder={platzhalter}
                        className="px-2 py-1 border border-slate-300 rounded text-xs w-28"
                    />
                    {(vorschlaege ?? [])
                        .filter(v => !werte.includes(v))
                        .slice(0, 12)
                        .map(v => (
                            <button
                                key={v}
                                type="button"
                                onClick={() => zufuegen(v)}
                                className="px-2 py-1 border border-dashed border-slate-300
                                           rounded text-xs text-slate-500 hover:bg-slate-50"
                            >
                                + {v}
                            </button>
                        ))}
                </div>
            )}
        </div>
    );
};

/** Überschrift innerhalb einer Karte. */
export const Untertitel: React.FC<{ text: string; hinweis?: string }> = ({ text, hinweis }) => (
    <div className="mb-1.5">
        <p className="text-[11px] font-black uppercase tracking-wider text-slate-400">{text}</p>
        {hinweis && <p className="text-[11px] text-slate-500">{hinweis}</p>}
    </div>
);

export const Leerzeile: React.FC<{ text: string }> = ({ text }) => (
    <p className="text-sm text-slate-400 py-3 text-center">{text}</p>
);
